import { keepPreviousData, queryOptions } from "@tanstack/react-query"

import { getIsomorphicSupabaseClient } from "@repo/supabase/isomorphic-client"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import { createResourcesService } from "~/modules/resources/resources.service"

export const getCurrentResourceQueryOptions = () =>
  queryOptions({
    queryKey: ["current-resource"],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.user.id) {
        throw new Error("User not authenticated")
      }

      const resourcesService = createResourcesService(supabase)
      return resourcesService.getOneExtended({ unitId: session?.user.id })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })

export const getExtendedResourceBySlugQueryOptions = ({
  slug,
}: {
  slug: string
}) =>
  queryOptions({
    queryKey: ["extended-resources", slug],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const resourcesService = createResourcesService(supabase)
      return resourcesService.getOneExtendedBySlug({ slug })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })

export const getExtendedResourcesQueryOptions = ({
  filters,
}: {
  filters?: DataTableBaseSearch & {
    business_entity_slugs?: string[]
    client_slugs?: string[]
  }
} = {}) =>
  queryOptions({
    queryKey: filters
      ? ["extended-resources", filters]
      : ["extended-resources"],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const resourcesService = createResourcesService(supabase)
      return resourcesService.getManyExtended(filters)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })

export const getResourceProjectsQueryOptions = ({
  resourceUnitId,
}: {
  resourceUnitId: string
}) =>
  queryOptions({
    queryKey: ["resource-projects", resourceUnitId],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const resourcesService = createResourcesService(supabase)
      return resourcesService.getProjects({ resourceUnitId })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })
